import type React from 'react';

export type FontDisplayMethod = 'auto' | 'block' | 'swap' | 'fallback' | 'optional';

export interface FontRegistryTypefaceSource {
  path: string;
  weight?: string;
  display?: FontDisplayMethod;
  preload?: boolean;
}

export interface FontRegistryTypeface {
  name: string;
  src: FontRegistryTypefaceSource[];
  variable: string;
}

export interface IFontRegistry {
  fonts: FontRegistryTypeface[];
  seal: () => void;
}

let sealed = false;

export const FontRegistry: IFontRegistry = {
  fonts: [],
  seal: () => {
    sealed = true;
  },
};

const fontMimeTypes = {
  woff2: 'font/woff2',
  woff: 'font/woff',
  ttf: 'font/ttf',
  otf: 'font/otf',
  eot: 'application/vnd.ms-fontobject',
  svg: 'image/svg+xml',
};

// NextJS _document.tsx helpers
const getFontMimeType = (filePath: string) => {
  let mimeType: string;

  Object.keys(fontMimeTypes).forEach((ext) => {
    if (filePath.endsWith(ext)) {
      mimeType = fontMimeTypes[ext];
    }
  });

  return mimeType;
};

const getFontFileExtension = (filePath: string) => {
  let extension: string;

  Object.keys(fontMimeTypes).forEach((ext) => {
    if (filePath.endsWith(ext)) {
      extension = ext;
    }
  });

  return extension;
};

const generateFontFaceCSS = () => {
  return FontRegistry.fonts
    .map((font) =>
      font.src
        .map(
          (file) => `\n@font-face {
  font-family: '${font.name}';
  src: url('${file.path}') format('${getFontFileExtension(file.path)}');
  font-display: ${file.display ?? 'swap'};
  ${file.weight !== undefined ? `font-weight: ${file.weight};` : ''}
}\n`,
        )
        .join(''),
    )
    .join('');
};

const generateFontVariableCSS = () => {
  return `\n:root {${FontRegistry.fonts.map((font) => `\n  ${font.variable}: '${font.name}';\n`).join('')}}\n`;
};

export const FontRegistryPreloadHeaders: React.FC = () => {
  return FontRegistry.fonts.map((font) =>
    font.src
      .map((file) =>
        file.preload ? (
          <link
            key={file.path}
            rel='preload'
            href={file.path}
            as='font'
            type={getFontMimeType(file.path)}
            crossOrigin='anonymous'
            data-font-registry
          />
        ) : null,
      )
      .filter((element) => element !== null),
  );
};

export const FontRegistryStyles: React.FC = () => {
  return (
    <style
      data-font-registry
      dangerouslySetInnerHTML={{
        __html: generateFontFaceCSS() + generateFontVariableCSS(),
      }}
    />
  );
};

// Developer-facing functions
export const registerFont = (font: FontRegistryTypeface) => {
  if (sealed) return;
  FontRegistry.fonts.push(font);
};
